import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "@/components/Table/Table";
import Pagination from "@/components/Table/Pagination";
import type { Column } from "@/types/common";
import userService from "@/services/api/user";
import type { User } from "@/services/api/user";
import { USER_TYPE_OPTIONS } from "@/services/api/user";
import { humanizeEnumValue } from "@/lib/utils";
import { getDisplayName } from "@/lib/user";
import { getMemberStatusTone } from "@/lib/statusTone";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getUsers } from "@/store/slices/userSlice";
import ExportConfirmationModal from "@/components/ExportConfirmationModal/ExportConfirmationModal";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import Eye from "@/components/SVGComponents/Eye";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import MobileSearchBar from "@/components/MobileSearchBar/MobileSearchBar";
import FilterChips from "@/components/FilterChips/FilterChips";
import MobileList, { MobileListPanel, MobileListRow } from "@/components/MobileList/MobileList";
import { TableCellTooltip } from "@/components/Tooltip";
import toast from "@/lib/toast";
import routes from "@/routes/routes";
import InviteDirectlyModal from "./components/InviteDirectlyModal";
import styles from "./Waitlist.module.scss";

// Same GET /users endpoint and shape as pages/Members/Members.tsx — only
// `screenType` differs. See docs/decisions.md #18-19 for what's confirmed
// real about this endpoint vs. still-a-guess. `usertype` dropped per the
// user, same as Members.tsx — was filtering to "FOUNDER" only.
const WAITLIST_QUERY_BASE = { screenType: "waitlist" };

// Values match the real `UserStatus` enum given directly by the user
// (see services/api/user.ts) — "BLOCK", not "BLOCKED" as first guessed.
const STATUS_OPTIONS = [
  { label: "Under review", value: "UNDER_REVIEW" },
  { label: "Rejected", value: "REJECTED" },
];

// Only two real actions here: "View record" navigates the same place a
// row click does (see the table's own `onRowClicked`), and "Block" hits
// the real `PATCH /users/{id}/status` endpoint (see userService.blockUser)
// behind a confirm modal — left out entirely for a row already blocked.
const getRowActions = (
  user: User,
  onViewClick: (user: User) => void,
  onBlockClick: (user: User) => void,
): RowAction[] => {
  const actions: RowAction[] = [
    {
      key: "view",
      label: "View record",
      icon: <Eye width={16} height={16} strokeWidth={1.75} />,
      onClick: () => onViewClick(user),
    },
  ];

  if (user.status !== "BLOCK") {
    actions.push({
      key: "block",
      label: "Block",
      icon: <CrossIcon withBg={false} stroke="currentColor" strokeWidth={1.5} width={14} height={14} />,
      tone: "danger",
      onClick: () => onBlockClick(user),
    });
  }

  return actions;
};

const Waitlist = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { users, totalUsers, isFetching } = useAppSelector((state) => state.user);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [pageSelected, setPageSelected] = useState(1);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [userToBlock, setUserToBlock] = useState<User | null>(null);
  const rowsPerPageValue = 10;

  const formConfig = useMemo(
    () => ({
      status: {
        controlType: "select" as const,
        label: "Status",
        isControlled: true,
        props: { options: STATUS_OPTIONS },
      },
      role: {
        controlType: "select" as const,
        label: "Role",
        isControlled: true,
        props: { options: USER_TYPE_OPTIONS },
      },
    }),
    [],
  );

  const fetchWaitlist = useCallback(() => {
    dispatch(
      getUsers({
        search: debouncedSearch || undefined,
        pageNo: pageSelected,
        perPage: rowsPerPageValue,
        ...WAITLIST_QUERY_BASE,
        status: statusFilter || undefined,
        userType: roleFilter || undefined,
      }),
    );
  }, [dispatch, debouncedSearch, pageSelected, statusFilter, roleFilter]);

  useEffect(() => {
    fetchWaitlist();
  }, [fetchWaitlist]);

  const goToApplicationReview = (row: User) =>
    // The clicked row's real data is passed via router state (see
    // ApplicationReview.tsx) rather than re-fetched there — no
    // GET-by-id endpoint exists, and this row is already the real
    // thing that was clicked.
    navigate(routes.privateRoutes.WAITLIST_APPLICATION_REVIEW.path.replace(":memberId", String(row.id)), {
      state: row,
    });

  const handleConfirmBlock = async () => {
    if (!userToBlock) return;
    const name = getDisplayName(userToBlock);
    try {
      await userService.blockUser(userToBlock.id);
      toast.success(`${name} has been blocked.`);
      fetchWaitlist();
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  };

  usePageSubtitle(isFetching ? "Loading waitlist…" : `${totalUsers} on the waitlist`);

  const columns: Column<User>[] = [
    {
      name: "Member",
      width: "25%",
      grow: 0,
      cell: (row) => (
        <div className={styles.member} data-tag="allowRowEvents">
          <TableCellTooltip value={getDisplayName(row)} textClassName={styles.memberName} />
          <TableCellTooltip value={row.email || "-"} textClassName={styles.memberSubtitle} />
        </div>
      ),
    },
    {
      name: "Role",
      width: "20%",
      grow: 0,
      cell: (row) => (row.userType ? <StatusBadge label={humanizeEnumValue(row.userType)} tone="brand" /> : "-"),
    },
    { name: "City", width: "20%", grow: 0, selector: (row) => row.cityName || row.location || "-" },
    // { name: "Mobile", width: "14%", grow: 0, selector: (row) => row.mobileNo || "-" },
    {
      name: "Status",
      width: "23%",
      grow: 0,
      cell: (row) => (
        <StatusBadge
          label={row.status ? humanizeEnumValue(row.status) : "-"}
          tone={getMemberStatusTone(row.status)}
        />
      ),
    },
    {
      name: "Action",
      width: "8%",
      grow: 0,
      ignoreRowClick: true,
      cell: (row) => (
        <RowActionsMenu
          actions={getRowActions(row, goToApplicationReview, setUserToBlock)}
          ariaLabel={`Actions for ${getDisplayName(row)}`}
        />
      ),
    },
  ];

  const handleTableClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    if (target.getAttribute("data-tag")) return;

    // Ignore clicks on buttons, links, inputs, or menu items (e.g. actions menu)
    const isInteractive = target.closest(
      "button, a, input, select, textarea, [role='button'], [data-ignore-row-click='true']",
    );
    if (isInteractive) return;

    // Ignore clicks inside columns configured to ignore row clicks (like the Actions column)
    const cell = target.closest<HTMLElement>(".rdt_TableCell");
    if (cell && !cell.getAttribute("data-tag")) {
      return;
    }

    target.setAttribute("data-tag", "allowRowEvents");
  };

  return (
    <>
      {/* Desktop View */}
      <div className={styles.desktopView} onClickCapture={handleTableClickCapture}>
        <Table<User>
          columns={columns}
          data={users}
          progressPending={isFetching}
          pointerOnHover
          onRowClicked={goToApplicationReview}
          header={{
            search,
            setSearch,
            searchPlaceholder: "Search waitlist",
            formConfig,
            onFilterSubmit: (data) => {
              setStatusFilter((data.status as string | undefined) ?? null);
              setRoleFilter((data.role as string | undefined) ?? null);
              setPageSelected(1);
            },
            onExport: () => setIsExportModalOpen(true),
            // addButtonTitle: "INVITE DIRECTLY",
            onAddClick: () => setIsInviteModalOpen(true),
          }}
          paginationProps={{
            isPagination: true,
            pageSelected,
            totalCount: totalUsers,
            rowsPerPageValue,
            setPageSelected,
          }}
        />
      </div>

      {/* Mobile View: same search/filter/page state as the table above */}
      <div className={styles.mobileView}>
        <MobileListPanel
          actions={[
            {
              key: "export",
              label: "EXPORT",
              icon: <DownloadIcon />,
              outlined: true,
              onClick: () => setIsExportModalOpen(true),
            },
            { key: "invite", label: "INVITE", icon: <PlusIcon />, onClick: () => setIsInviteModalOpen(true) },
          ]}
        >
          <MobileSearchBar value={search} onChange={setSearch} placeholder="Search by name, email or company" />
          <FilterChips
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setPageSelected(1);
            }}
            allLabel="All statuses"
            ariaLabel="Filter by status"
          />
          <FilterChips
            options={USER_TYPE_OPTIONS}
            value={roleFilter}
            onChange={(value) => {
              setRoleFilter(value);
              setPageSelected(1);
            }}
            allLabel="All roles"
            ariaLabel="Filter by role"
          />
        </MobileListPanel>

        <MobileList
          isLoading={isFetching}
          isEmpty={users.length === 0}
          emptyTitle="No applications found"
          emptyCaption={search || statusFilter || roleFilter ? "Try clearing your search or filters." : "The waitlist is empty."}
        >
          {users.map((user) => (
            <MobileListRow
              key={user.id}
              title={getDisplayName(user)}
              badge={
                <StatusBadge
                  label={user.status ? humanizeEnumValue(user.status) : "-"}
                  tone={getMemberStatusTone(user.status)}
                />
              }
              meta={[
                { key: "email", value: user.email },
                { key: "persona", value: user.userType, emphasis: true },
                { key: "city", value: user.cityName || user.location },
              ]}
              actions={getRowActions(user, goToApplicationReview, setUserToBlock)}
              onClick={() => goToApplicationReview(user)}
              ariaLabel={`Review application from ${getDisplayName(user)}`}
            />
          ))}
        </MobileList>

        {totalUsers > rowsPerPageValue && (
          <div className={styles.mobilePagination}>
            <Pagination
              pageSelected={pageSelected}
              totalCount={totalUsers}
              rowsPerPageValue={rowsPerPageValue}
              setPageSelected={setPageSelected}
            />
          </div>
        )}
      </div>

      <ExportConfirmationModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        fallbackFileName="waitlist"
        fetchExportFile={() =>
          userService.exportUsers({
            ...WAITLIST_QUERY_BASE,
            search: debouncedSearch || undefined,
            status: statusFilter || undefined,
            userType: roleFilter || undefined,
          })
        }
      />
      <InviteDirectlyModal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} />
      <ConfirmDeleteModal
        isOpen={userToBlock !== null}
        onClose={() => setUserToBlock(null)}
        onConfirm={handleConfirmBlock}
        headerTitle="Confirm Block"
        confirmLabel="Block"
        title="Block Member"
        message={
          userToBlock ? (
            <>
              Are you sure you want to block <strong>{getDisplayName(userToBlock)}</strong>? They will lose access to
              their account.
            </>
          ) : null
        }
      />
    </>
  );
};

export default Waitlist;
